import React, { useState } from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import { BatteryIcon, ElectricCarIcon, EmcIcon, HeatpumpIcon, IoInterfaceIcon, MeterIcon } from 'src/Libs/flow/icons/SettingsIcons'
import { AddChargingStation } from './AddChargingStation'
import { AddEmc } from './AddEmc'
import { AddIoInterface } from './AddIoInterface'
import { AddStorage } from './AddStorage'
import { TFunction } from 'i18next'
import { getAvailableIo, getIsHeatpumpAvailable, hasAvailableIo } from 'src/Libs/flow/installation/utils'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { HideDirtyStateProvider } from '../../../../providers'
import { InstallationFormModel, StorageSystem } from 'src/Libs/flow/installation/forms'
import { EvseReference } from 'src/Libs/flow/parsers'
import { countryCodes } from 'src/Libs/flow/constants'
import { MAXIMUM_EVSES_PER_INSTALLATION, MAXIMUM_METERS_PER_INSTALLATION } from '../../../../constants'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { makeStyles } from 'tss-react/mui'
import { MeterDeviceType } from '../../../../powermeter/types'
import { AddHeatpump } from './AddHeatpump'
import { AddMeter } from './AddMeter'
import { useIsFarmFeatureAvailableForFlowInstallation } from 'src/Hooks'

const useStyles = makeStyles()(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingBottom: 0,
    marginBottom: 0,
  },
}))

const addDeviceTabs = ['EMC', 'Storage System', 'Charging Station', 'Hvac', 'Sub Meter', 'I/O Interface'] as const

export type AddDeviceTab = (typeof addDeviceTabs)[number]

const addDeviceConfigMap: Record<AddDeviceTab, { titleTranslationKey: string; tabLabelTranslationKey: string; icon: React.ReactElement }> = {
  EMC: {
    titleTranslationKey: 'installation.productEditor.edit.emc',
    tabLabelTranslationKey: 'installation.productEditor.deviceTypes.emc',
    icon: <EmcIcon />,
  },
  'Storage System': {
    titleTranslationKey: 'installation.productEditor.edit.storage',
    tabLabelTranslationKey: 'installation.types.storage',
    icon: <BatteryIcon />,
  },
  'Charging Station': {
    titleTranslationKey: 'installation.productEditor.edit.evse',
    tabLabelTranslationKey: 'installation.productEditor.deviceTypes.evse',
    icon: <ElectricCarIcon />,
  },
  Hvac: {
    titleTranslationKey: 'installation.productEditor.edit.heatpump',
    tabLabelTranslationKey: 'installation.heatpumpEditor.emptyTitle',
    icon: <HeatpumpIcon />,
  },
  'Sub Meter': {
    titleTranslationKey: 'installation.productEditor.edit.submeter',
    tabLabelTranslationKey: 'installation.productEditor.deviceTypes.submeter',
    icon: <MeterIcon />,
  },
  'I/O Interface': {
    titleTranslationKey: 'installation.productEditor.edit.ioInterface',
    tabLabelTranslationKey: 'installation.productEditor.deviceTypes.ioInterface',
    icon: <IoInterfaceIcon />,
  },
}
//#region Main dialog
export interface AddDeviceDialogProps {
  installation: InstallationFormModel
  /** If null, modal will open at the tab of the first available device. */
  initialTab: AddDeviceTab | null
  addEmc: (name: string) => void
  addStorage: (name: string, previousStorageSystem: StorageSystem | null) => void
  addWallbox: (name: string, reference: EvseReference) => void
  addHeatpump: (name: string) => void
  addMeter: (name: string, deviceType: MeterDeviceType, peakPower: number) => void
  addIoInterface: (name: string, deviceType: string) => void
  onClose: () => void
}
//#endregion

//#region AddDevice
export const AddDeviceDialog: React.FC<AddDeviceDialogProps> = ({
  initialTab,
  installation,
  addEmc,
  addStorage,
  addWallbox,
  addHeatpump,
  addMeter,
  addIoInterface,
  onClose: closeDialog,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const isFarmFeatureAvailableForFlowInstallation = useIsFarmFeatureAvailableForFlowInstallation()

  const { addEmcError, addStorageError, addChargingStationError, addHeatpumpError, addMeterError, addIoInterfaceError } = getAddDeviceSettings(
    installation,
    t,
    isFarmFeatureAvailableForFlowInstallation
  )

  const [tab, setTab] = useState<AddDeviceTab>(() => {
    // Get the initial tab. If one is not provided, find the first tab without a limit error.
    if (initialTab) return initialTab
    const errors = [addEmcError, addStorageError, addChargingStationError, addHeatpumpError, addMeterError, addIoInterfaceError]
    return addDeviceTabs.find((_tab, index) => errors[index] === null) ?? 'EMC'
  })

  const handleTabChange = (_event: React.SyntheticEvent, newValue: AddDeviceTab): void => {
    setTab(newValue)
  }

  const availableIo = getAvailableIo(
    installation.ioInterfaces,
    installation.heatpump,
    installation.generalSettings.location.country === countryCodes.germany
  )

  return (
    <Dialog
      title={t(addDeviceConfigMap[tab].titleTranslationKey)}
      maxWidth="md"
      divider={false}
      useActions={false}
      contentClassName={classes.container}
    >
      <Tabs value={tab} onChange={handleTabChange} variant="scrollable" allowScrollButtonsMobile>
        {addDeviceTabs.map(tab => (
          <Tab
            key={tab}
            value={tab}
            label={t(addDeviceConfigMap[tab].tabLabelTranslationKey)}
            icon={addDeviceConfigMap[tab].icon}
            iconPosition="start"
          />
        ))}
      </Tabs>

      <HideDirtyStateProvider hideDirtyState>
        <AddDeviceDialogTab isActive={tab === 'EMC'}>
          <AddEmc error={addEmcError} closeDialog={closeDialog} addEmc={addEmc} />
        </AddDeviceDialogTab>

        <AddDeviceDialogTab isActive={tab === 'Storage System'}>
          <AddStorage
            installationId={installation.id}
            error={addStorageError}
            closeDialog={closeDialog}
            storages={installation.storageDevices}
            addStorage={addStorage}
          />
        </AddDeviceDialogTab>

        <AddDeviceDialogTab isActive={tab === 'Charging Station'}>
          <AddChargingStation
            error={addChargingStationError}
            closeDialog={closeDialog}
            addWallbox={addWallbox}
            chargingStations={installation.chargingStations}
          />
        </AddDeviceDialogTab>

        <AddDeviceDialogTab isActive={tab === 'Hvac'}>
          <AddHeatpump error={addHeatpumpError} closeDialog={closeDialog} heatpump={installation.heatpump} addHeatpump={addHeatpump} />
        </AddDeviceDialogTab>

        <AddDeviceDialogTab isActive={tab === 'Sub Meter'}>
          <AddMeter error={addMeterError} closeDialog={closeDialog} meters={installation.meters} addMeter={addMeter} />
        </AddDeviceDialogTab>

        <AddDeviceDialogTab isActive={tab === 'I/O Interface'}>
          <AddIoInterface
            availableIo={availableIo}
            error={addIoInterfaceError}
            closeDialog={closeDialog}
            ioInterfaces={installation.ioInterfaces}
            storages={installation.storageDevices}
            addIoInterface={addIoInterface}
          />
        </AddDeviceDialogTab>
      </HideDirtyStateProvider>
    </Dialog>
  )
}
//#endregion

//#region Add Dialog Tab
const AddDeviceDialogTab: React.FC<{
  isActive: boolean
  children: React.ReactNode
}> = ({ isActive, children }) => {
  return <Box display={isActive ? 'contents' : 'none'}>{children}</Box>
}
//#endregion

//#region Add Device Settings
interface AddDeviceSettings {
  addEmcError: string | null
  addStorageError: string | null
  addChargingStationError: string | null
  addHeatpumpError: string | null
  addMeterError: string | null
  addIoInterfaceError: string | null
}

const getAddDeviceSettings = (
  installation: InstallationFormModel,
  t: TFunction,
  isFarmFeatureAvailableForFlowInstallation: boolean
): AddDeviceSettings => {
  const hasEmc = installation.emcDevice !== null

  if (!hasEmc) {
    const noEmcError = t('installation.productEditor.scanNetwork.emcId')

    return {
      addEmcError: null,
      addStorageError: noEmcError,
      addChargingStationError: noEmcError,
      addHeatpumpError: noEmcError,
      addMeterError: noEmcError,
      addIoInterfaceError: noEmcError,
    }
  }

  const isGerman = installation.generalSettings.location.country === countryCodes.germany

  const hasMaxChargingStations =
    installation.chargingStations.modbusTcp.length + installation.chargingStations.eebus.length >= MAXIMUM_EVSES_PER_INSTALLATION

  const hasHeatpump = installation.heatpump !== null
  const isHeatpumpAvailable = getIsHeatpumpAvailable(installation.ioInterfaces, installation.heatpump, isGerman)
  const hasMaxMeters = installation.meters.length >= MAXIMUM_METERS_PER_INSTALLATION
  const hasMaxIoInterfaces = !hasAvailableIo(getAvailableIo(installation.ioInterfaces, installation.heatpump, isGerman))
  const addStorageError = !isFarmFeatureAvailableForFlowInstallation
    ? installation.storageDevices.length > 0
      ? t('installation.productEditor.storageLimit')
      : null
    : null

  return {
    addEmcError: t('installation.productEditor.emcLimit'),
    addStorageError,
    addChargingStationError: hasMaxChargingStations ? t('installation.productEditor.evseLimit') : null,
    addHeatpumpError: getHeatpumpErrorKey(hasHeatpump, isHeatpumpAvailable, t),
    addMeterError: hasMaxMeters ? t('installation.productEditor.submeterLimit') : null,
    addIoInterfaceError: hasMaxIoInterfaces ? t('installation.productEditor.ioInterfaceLimit') : null,
  }
}

const getHeatpumpErrorKey = (hasHeatpump: boolean, isAvailable: boolean, t: TFunction): string | null => {
  if (hasHeatpump) return t('installation.productEditor.heatpumps-limit')
  if (!isAvailable) return t('installation.productEditor.sg-ready-limit')
  return null
}
//#endregion
